import type { Disposable, Model } from '@patsnap/tahiti-core';
import type { RetrievalElement } from '../apis/retrieval-elements-api';

/**
 * 图片转文本结果中的图片项
 */
export interface ImageToTextImageItem {
    /** 存储 URL */
    stored_url: string;
    /** 签名 URL（可选） */
    signed_url?: string;
    /** 图片转文本结果（可选） */
    image_to_text?: string;
    /** 文件大小（字节） */
    file_size?: number;
}

/**
 * 图片转文本结果中的文件项
 */
export interface ImageToTextFileItem {
    /** 原始文件名 */
    original_file_name?: string;
    /** 存储 URL */
    stored_url: string;
    /** 签名 URL（可选） */
    signed_url?: string;
    /** 文件大小（字节） */
    file_size?: number;
}

/**
 * 图片转文本模型类型
 */
export type ImageToTextModelType = 'qwen-2_5' | 'gemini-2.5-flash' | 'qwen-vl-max';

/**
 * 图片转文本结果
 */
export interface ImageToTextResult {
    /** 模型类型，默认值为 qwen-vl-max */
    model_type?: ImageToTextModelType;
    /** 转换后的文本（可选） */
    text?: string;
    /** 图片列表（必选） */
    images: ImageToTextImageItem[];
    /** 文件列表（可选） */
    files?: ImageToTextFileItem[];
}

/**
 * 国际化文本
 */
export interface I18nText {
    zh: string;
    en: string;
}

/**
 * 查询项
 */
export interface QueryItem {
    /**
     * 检索式编号
     * @example "S1"
     */
    query_no?: string;
    /**
     * 使用检索式编号
     * @example ["S1", "S2", "S3", "S4", "S5"]
     */
    using_query_no?: string[];
    /**
     * 检索式别名
     * @example "S1 AND S2 AND S3 AND S4 AND S5"
     * @example "SEMANTIC:旋具接头包括具有容纳腔的旋具本体和设置于容纳腔的多根弹性杆"
     */
    alias_query: string;
    /**
     * 检索式
     */
    query: string;
    /**
     * 数量
     */
    count: number;
}

/**
 * 专利文档项
 */
export interface PatentDocItem {
    patent_id: string;
    feature_match_num: number;
    public_score: number;
    /**
     * 是否在上一轮中cc对比过
     */
    prev_hit: boolean;
    score: number;
}
export interface PaperAndWebsiteDocItem {
    doc_id: string;
    doc_type: string;
    title: string
    url: string;
    feature_match_num: number;
    public_score: number;
    score: number;
}

export type DocItem = PatentDocItem | PaperAndWebsiteDocItem
/**
 * 比较结果
 */
export interface ComparisonResults {
    comparison_doc_num: number;
    find_doc_num: number;
    documents: DocItem[];
}



/**
 * 检索式构建过程项
 */
export interface BlockQueryProcess {
    /**
     * 构建轮次
     */
    attempt: number;
    /**
     * 构建逻辑
     */
    logic: I18nText;
    /**
     * 检索式
     */
    query: string;
    /**
     * 数量
     */
    count: number;
    /**
     * 检索式构建结论
     */
    conclusion: I18nText;
}

/**
 * 检索块查询
 */
export interface BlockQuery {
    /**
     * 检索类型
     * @description 基础检索、组合检索
     */
    type: 'basic' | 'combined';
    /**
     * 技术特征描述
     */
    feature_text_original: string;
    alias_query: string
    /**
     * 最终检索式
     */
    query: string;
    /**
     * 数量
     */
    count: number;
    /**
     * 是否构建成功
     */
    status: string;
    /**
     * 构建过程（构建结束后，此过程网站折叠）
     */
    process?: BlockQueryProcess[];
}

/**
 * 检索进度结果
 */
export interface RetrievalProgressResult {
    /**
     * 检索标题
     */
    title: I18nText;
    /**
     * 检索思路
     */
    query_thinking: I18nText;
    /**
     * 检索式思路
     */
    block_query_thinking: I18nText;
    /**
     * 检索式
     */
    block_query: Record<string, BlockQuery>;
    /**
     * 检索式
     */
    queries: QueryItem[];
    /**
     * 比较结果
     */
    comparison_results: ComparisonResults;
    /**
     * 下一步计划
     */
    next_plan: {
        thinking: I18nText;
    };
    /**
     * 检索类型
     */
    type: string;
    /**
     * 花费
     */
    cost: number;
    /**
     * 状态
     */
    status: string;
}

export interface RetrievalProgressComparisonFeature {
    comparison_feature: string;
    public_score: number;
    score: number;
    tech_feature: string;
    similar: boolean;
    type: TechFeatureType;
    original_type: TechFeatureType;
    reason?: string;
    original_reason?: string;
}

/**
 * 特征类型
 * @description 特征类型：public 现有技术、obvious 显而易见、not_public 非现有技术
 */
export type TechFeatureType = 'public' | 'obvious' | 'not_public';

export interface RetrievalProgressFinalResult {
    feature_match_num: number;
    comparative_literature_type: 'X' | 'Y' | 'A';
    type: TechFeatureType;
    features: RetrievalProgressComparisonFeature[];
    patent_id: string;
    public_score: number;
    selected: boolean;
    most_similar: boolean;
    user_added: boolean;
    /** 提取特征所用到的语言 */
    lang: string;
}

/**
 * 检索进度文档结果（基础类型，用于论文和网站）
 * @description 与专利结果类似，但使用 doc_id 替代 patent_id，并包含文档标题
 */
export interface RetrievalProgressDocResult {
    feature_match_num: number;
    comparative_literature_type: 'X' | 'Y' | 'A';
    type: TechFeatureType;
    features: RetrievalProgressComparisonFeature[];
    doc_id: string;
    doc_title: string;
    /** 文档 URL（可选，用于论文和网站） */
    doc_url?: string;
    public_score: number;
    selected: boolean;
    most_similar: boolean;
    user_added: boolean;
    /** 提取特征所用到的语言 */
    lang: string;
    /**
     * 发表时间（可选）
     * @description 格式为 YYYYMMDD 的数字，如 20201212
     */
    pbd?: number;
}

/**
 * 检索进度论文结果
 * @description 继承文档基础类型，新增论文专用字段
 */
export interface RetrievalProgressPaperResult extends RetrievalProgressDocResult {
    /** 期刊名称（论文专用） */
    journal?: string;
    /** 作者（论文专用） */
    author?: string;
    /** 摘要（论文专用） */
    abstract?: string;
}

/**
 * 检索进度网站结果
 * @description 继承文档基础类型，新增网站专用字段
 */
export interface RetrievalProgressWebsiteResult extends RetrievalProgressDocResult {
    /** 来源网站（网站专用） */
    source?: string;
}
export interface RetrievalProgressFeatureAnalysisResult {
    block_query: Record<string, BlockQuery>;
    keywords_extract_result: RetrievalElement[];
    /**
     * 是否启用同族合并
     */
    family_merge_enabled?: boolean;
}
/**
 * 检索进度状态
 */
export interface RetrievalProgressStateV2 {
    feature_analysis_result: RetrievalProgressFeatureAnalysisResult;
    final_result: RetrievalProgressFinalResult[];
    /**
     * 论文检索结果
     */
    paper_result?: RetrievalProgressPaperResult[];
    /**
     * 网站检索结果
     */
    website_result?: RetrievalProgressWebsiteResult[];
    /**
     * 检索的步骤
     */
    process_dict_keys: string[];
    /**
     * 检索的步骤详情数据
     */
    process_dict: Record<string, RetrievalProgressResult>;
    /**
     * 检索耗时，单位：秒
     */
    task_cost: number;
    task_id: string;
    task_status: 'running' | 'success' | 'canceled' | 'failed';
    cc_docs_num: number;
    excute_times: number;
    update_ts: string;
    /**
     * 图片转文本结果（可选）
     * 当用户上传图片进行识别时，此字段包含识别结果
     */
    image_to_text_result?: ImageToTextResult;
}

/**
 * 检索进度模型
 * @description 存储检索进度状态
 */
export interface RetrievalProgressModelV2 extends Model<RetrievalProgressStateV2>, Disposable {

}

export const RetrievalProgressModelV2 = Symbol.for('RetrievalProgressModelV2');
